/**
 * Iter 193 — GMP QMS Phase 1 tabs: Deviations, Complaints, OOS.
 *
 * Loaded lazily from GMP.jsx. Each tab is self-contained, uses api.get/post/put/del,
 * follows the same UX language as the rest of the GMP Portal (filter bar, badges,
 * detail modals, PDF export not in this phase — global GMP PDF redesign is Phase 3).
 *
 * ALL "CAPA Aç" flows are MANUAL — no automatic CAPA creation from this module.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Btn, Badge, Input, Select, Textarea, Modal } from "@/components/UI";
import GmpEvidencePanel from "@/components/GmpEvidencePanel";
import { openPdfPreview } from "@/lib/pdfPreview";
import { FileText } from "lucide-react";
import { AlertTriangle, MessageCircleWarning, FlaskConical, Plus, Search, Link2, Shield, X, ChevronRight } from "lucide-react";
import { todayTR } from "@/lib/date";

const today = () => todayTR();
const fmtDate = (iso) => (iso || "").slice(0, 10);
const fmtDateTime = (iso) => (iso || "").slice(0, 19).replace("T", " ");

const SEVERITY_LEVEL = { critical: "missing", major: "critical", minor: "attention" };
const STATUS_LEVEL = {
  open: "attention", investigating: "critical", awaiting_customer: "attention",
  phase_1: "critical", phase_2: "critical",
  closed: "ok", cancelled: "inactive",
};

const DEV_CATEGORIES = [
  { k: "production", l: "Üretim" }, { k: "qc", l: "Kalite Kontrol" },
  { k: "equipment", l: "Ekipman" }, { k: "documentation", l: "Doküman/SOP" },
  { k: "personnel", l: "Personel" }, { k: "facility", l: "Tesis" },
  { k: "material", l: "Hammadde/Malzeme" }, { k: "other", l: "Diğer" },
];
const SEVERITIES = [{ k: "critical", l: "Kritik" }, { k: "major", l: "Majör" }, { k: "minor", l: "Minör" }];
const COMPLAINT_TYPES = [
  { k: "quality", l: "Kalite" }, { k: "service", l: "Hizmet" }, { k: "packaging", l: "Ambalaj" },
  { k: "label", l: "Etiket" }, { k: "delivery", l: "Teslimat" }, { k: "other", l: "Diğer" },
];
const OOS_DISPOSITIONS = [
  { k: "pending", l: "Beklemede" }, { k: "release", l: "Serbest Bırak" },
  { k: "reject", l: "Reddet" }, { k: "rework", l: "Yeniden İşle" }, { k: "hold", l: "Bekletme" },
];


/* ============= COMMON CAPA MODAL ============= */
function OpenCapaModal({ open, onClose, onConfirm, defaultTitle, defaultDescription }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("high");
  useEffect(() => { if (open) { setTitle(defaultTitle || ""); setDescription(defaultDescription || ""); setPriority("high"); } }, [open, defaultTitle, defaultDescription]);
  return (
    <Modal open={open} onClose={onClose} title="🛡 CAPA Aç" size="md">
      <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
        Bu kayıt için manuel CAPA açılır. Açıldığında GMP Portalı → <b>CAPA</b> sekmesinde görünür.
        Kapatma için CAPA panelinden <b>Kök Neden + Önleyici Aksiyon</b> girilmesi gerekir.
      </div>
      <div className="space-y-3">
        <Input label="CAPA Başlık *" value={title} onChange={e => setTitle(e.target.value)} data-testid="capa-open-title"/>
        <Textarea label="Açıklama" rows={4} value={description} onChange={e => setDescription(e.target.value)} data-testid="capa-open-desc"/>
        <Select label="Öncelik" value={priority} onChange={e => setPriority(e.target.value)} data-testid="capa-open-priority">
          <option value="critical">Kritik</option>
          <option value="high">Yüksek</option>
          <option value="normal">Normal</option>
          <option value="low">Düşük</option>
        </Select>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Btn variant="secondary" onClick={onClose}>Vazgeç</Btn>
        <Btn onClick={() => onConfirm({ title, description, priority })} data-testid="capa-open-confirm">
          <Shield size={12}/> CAPA Aç
        </Btn>
      </div>
    </Modal>
  );
}


/* ============= DEVIATIONS TAB ============= */
export function DeviationsTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ status: "", severity: "", category: "", q: "" });
  const [modal, setModal] = useState(null); // { mode: 'create'|'edit'|'view', doc }
  const [closeModal, setCloseModal] = useState(null);
  const [capaModal, setCapaModal] = useState(null);
  const [productionOrders, setProductionOrders] = useState([]);
  const [lots, setLots] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [qcTests, setQcTests] = useState([]);

  const load = async () => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    const r = await api.get("/gmp/deviations", params);
    setRows(r || []);
  };
  useEffect(() => { load(); }, [f.status, f.severity, f.category]);
  useEffect(() => {
    // preload ref lists (small — cached for modal dropdowns)
    Promise.all([
      api.get("/production-orders").catch(() => []),
      api.get("/inventory/lots").catch(() => []),
      api.get("/gmp/equipment").catch(() => []),
      api.get("/materials").catch(() => []),
      api.get("/qc-tests").catch(() => []),
    ]).then(([po, l, eq, m, q]) => {
      setProductionOrders(po || []); setLots(l || []); setEquipment(eq || []);
      setMaterials(m || []); setQcTests(q || []);
    });
  }, []);

  const openCreate = () => setModal({
    mode: "create",
    doc: { title: "", description: "", category: "production", severity: "major", occurred_at: today(), department: "" }
  });
  const openEdit = (doc) => setModal({ mode: "edit", doc: { ...doc } });
  const save = async () => {
    const doc = modal.doc;
    if (!doc.title?.trim() || !doc.description?.trim()) { alert("Başlık ve açıklama zorunlu"); return; }
    try {
      if (modal.mode === "create") await api.post("/gmp/deviations", doc);
      else await api.put(`/gmp/deviations/${doc.id}`, doc);
      setModal(null); load();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
  };
  const invest = async (r) => { try { await api.post(`/gmp/deviations/${r.id}/investigate`); load(); } catch (e) { alert(e.response?.data?.detail); } };
  const doClose = async () => {
    const { doc, root_cause, resolution, closure_notes } = closeModal;
    if (!root_cause?.trim() || !resolution?.trim()) { alert("Kök Neden ve Çözüm zorunlu"); return; }
    try { await api.post(`/gmp/deviations/${doc.id}/close`, { root_cause, resolution, closure_notes }); setCloseModal(null); load(); }
    catch (e) { alert(e.response?.data?.detail || "Kapatılamadı"); }
  };
  const del = async (r) => { if (window.confirm(`Sapma ${r.number} arşivlensin mi?`)) { try { await api.del(`/gmp/deviations/${r.id}`); load(); } catch (e) { alert(e.response?.data?.detail); } } };
  const openCapa = (r) => setCapaModal({
    doc: r,
    defaultTitle: `CAPA — Sapma ${r.number}: ${r.title}`,
    defaultDescription: `Sapma No: ${r.number}\nKritiklik: ${r.severity}\nKategori: ${r.category}\nAçıklama: ${r.description || "-"}`,
  });
  const confirmCapa = async ({ title, description, priority }) => {
    try {
      await api.post(`/gmp/deviations/${capaModal.doc.id}/capa`, { title, description, priority });
      alert("CAPA açıldı. GMP → CAPA sekmesinden takip edebilirsiniz.");
      setCapaModal(null); load();
    } catch (e) { alert(e.response?.data?.detail || "CAPA açılamadı"); }
  };

  const filtered = useMemo(() => {
    const q = f.q?.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => (r.number + " " + r.title + " " + (r.description || "")).toLowerCase().includes(q));
  }, [rows, f.q]);

  return (
    <div>
      <div className="flex flex-wrap items-end gap-2 mb-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
        <div className="min-w-[200px] flex-1"><Input label="Arama" value={f.q} onChange={e => setF({ ...f, q: e.target.value })} placeholder="No / başlık / açıklama" data-testid="dev-search"/></div>
        <Select label="Durum" value={f.status} onChange={e => setF({ ...f, status: e.target.value })} data-testid="dev-filter-status">
          <option value="">Tümü</option><option value="open">Açık</option><option value="investigating">Soruşturmada</option>
          <option value="closed">Kapalı</option><option value="cancelled">İptal</option>
        </Select>
        <Select label="Kritiklik" value={f.severity} onChange={e => setF({ ...f, severity: e.target.value })} data-testid="dev-filter-severity">
          <option value="">Tümü</option>{SEVERITIES.map(s => <option key={s.k} value={s.k}>{s.l}</option>)}
        </Select>
        <Select label="Kategori" value={f.category} onChange={e => setF({ ...f, category: e.target.value })} data-testid="dev-filter-category">
          <option value="">Tümü</option>{DEV_CATEGORIES.map(c => <option key={c.k} value={c.k}>{c.l}</option>)}
        </Select>
        <div className="flex-1"/>
        {isAdmin && <Btn onClick={openCreate} data-testid="dev-new"><Plus size={14}/> Yeni Sapma</Btn>}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-slate-100 text-slate-700 text-xs">
            <tr>
              <th className="text-left px-3 py-2">No</th>
              <th className="text-left px-3 py-2">Başlık</th>
              <th className="text-left px-3 py-2">Kategori</th>
              <th className="text-left px-3 py-2">Kritiklik</th>
              <th className="text-left px-3 py-2">Bağlı Kayıt</th>
              <th className="text-left px-3 py-2">Durum</th>
              <th className="text-left px-3 py-2">Tarih</th>
              <th className="text-left px-3 py-2 w-72">İşlem</th>
            </tr>
          </thead>
          <tbody data-testid="dev-table">
            {filtered.map(r => {
              const refBadges = [];
              if (r.production_order_no_snapshot) refBadges.push({ l: "🏭 " + r.production_order_no_snapshot });
              if (r.lot_code_snapshot) refBadges.push({ l: "📦 " + r.lot_code_snapshot });
              if (r.qc_project_no_snapshot) refBadges.push({ l: "🧪 " + r.qc_project_no_snapshot });
              if (r.equipment_code_snapshot) refBadges.push({ l: "⚙ " + r.equipment_code_snapshot });
              if (r.material_code_snapshot) refBadges.push({ l: "🧪 " + r.material_code_snapshot });
              return (
                <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`dev-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{r.number}</td>
                  <td className="px-3 py-2 font-semibold text-slate-800">{r.title}</td>
                  <td className="px-3 py-2 text-xs">{DEV_CATEGORIES.find(x => x.k === r.category)?.l || r.category}</td>
                  <td className="px-3 py-2"><Badge level={SEVERITY_LEVEL[r.severity]}>{SEVERITIES.find(x => x.k === r.severity)?.l || r.severity}</Badge></td>
                  <td className="px-3 py-2 text-[11px]">
                    <div className="flex flex-wrap gap-1">
                      {refBadges.map((b, i) => <span key={i} className="bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded text-slate-700 font-mono">{b.l}</span>)}
                      {refBadges.length === 0 && <span className="text-slate-400 italic">—</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2"><Badge level={STATUS_LEVEL[r.status]}>{r.status}</Badge></td>
                  <td className="px-3 py-2 text-xs">{fmtDate(r.created_at)}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      <Btn size="sm" variant="ghost" onClick={() => setModal({ mode: "view", doc: r })} data-testid={`dev-view-${r.id}`}>Detay</Btn>
                      {r.status === "open" && <Btn size="sm" variant="secondary" onClick={() => invest(r)} data-testid={`dev-investigate-${r.id}`}>Soruşturmaya Al</Btn>}
                      {r.status !== "closed" && r.status !== "cancelled" && <Btn size="sm" onClick={() => setCloseModal({ doc: r, root_cause: r.root_cause || "", resolution: r.resolution || "", closure_notes: "" })} data-testid={`dev-close-${r.id}`}>Kapat</Btn>}
                      {r.status !== "closed" && <Btn size="sm" variant="secondary" onClick={() => openCapa(r)} data-testid={`dev-capa-${r.id}`}><Shield size={11}/> CAPA Aç</Btn>}
                      {r.status !== "closed" && r.status !== "cancelled" && <Btn size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`dev-edit-${r.id}`}>Düzenle</Btn>}
                      {/* Iter 202 §2 — Sapma controlled PDF (preview-first). */}
                      <Btn size="sm" variant="ghost" onClick={async () => {
                        try {
                          const blob = await api.getBlob(`/gmp/deviations/${r.id}/pdf`);
                          openPdfPreview(blob, `Sapma_${r.number || r.id}.pdf`);
                        } catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); }
                      }} data-testid={`dev-pdf-${r.id}`} title="Sapma PDF Önizleme"><FileText size={12}/></Btn>
                      <Btn size="sm" variant="danger" onClick={() => del(r)} data-testid={`dev-delete-${r.id}`}>Sil</Btn>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-400">Sapma kaydı yok.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* CREATE / EDIT MODAL */}
      <Modal open={!!modal && modal.mode !== "view"} onClose={() => setModal(null)} title={modal?.mode === "create" ? "Yeni Sapma" : `Sapma Düzenle ${modal?.doc?.number || ""}`} size="lg">
        {modal && (<div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Input label="Başlık *" value={modal.doc.title} onChange={e => setModal({ ...modal, doc: { ...modal.doc, title: e.target.value } })} data-testid="dev-form-title"/></div>
          <div className="col-span-2"><Textarea label="Açıklama *" rows={3} value={modal.doc.description} onChange={e => setModal({ ...modal, doc: { ...modal.doc, description: e.target.value } })} data-testid="dev-form-desc"/></div>
          <Select label="Kategori" value={modal.doc.category} onChange={e => setModal({ ...modal, doc: { ...modal.doc, category: e.target.value } })} data-testid="dev-form-category">
            {DEV_CATEGORIES.map(c => <option key={c.k} value={c.k}>{c.l}</option>)}
          </Select>
          <Select label="Kritiklik" value={modal.doc.severity} onChange={e => setModal({ ...modal, doc: { ...modal.doc, severity: e.target.value } })} data-testid="dev-form-severity">
            {SEVERITIES.map(s => <option key={s.k} value={s.k}>{s.l}</option>)}
          </Select>
          <Input label="Oluşma Tarihi" type="date" value={modal.doc.occurred_at || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, occurred_at: e.target.value } })} data-testid="dev-form-occurred"/>
          <Input label="Departman" value={modal.doc.department || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, department: e.target.value } })} data-testid="dev-form-department"/>
          <div className="col-span-2 border-t pt-2 mt-1 text-xs text-slate-500 font-semibold uppercase">Bağlı ERP Kayıtları (opsiyonel)</div>
          <Select label="Üretim Emri" value={modal.doc.production_order_id || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, production_order_id: e.target.value } })} data-testid="dev-form-po">
            <option value="">— Seç —</option>
            {productionOrders.slice(0, 200).map(p => <option key={p.id} value={p.id}>{p.prod_no} — {p.product_name || ""}</option>)}
          </Select>
          <Select label="Lot" value={modal.doc.lot_id || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, lot_id: e.target.value } })} data-testid="dev-form-lot">
            <option value="">— Seç —</option>
            {lots.slice(0, 200).map(l => <option key={l.id} value={l.id}>{l.lot_code}</option>)}
          </Select>
          <Select label="QC Testi" value={modal.doc.qc_test_id || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, qc_test_id: e.target.value } })} data-testid="dev-form-qc">
            <option value="">— Seç —</option>
            {qcTests.slice(0, 200).map(q => <option key={q.id} value={q.id}>{q.project_no_snapshot} — {q.test_type_label || q.test_type}</option>)}
          </Select>
          <Select label="Ekipman" value={modal.doc.equipment_id || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, equipment_id: e.target.value } })} data-testid="dev-form-equipment">
            <option value="">— Seç —</option>
            {equipment.slice(0, 200).map(x => <option key={x.id} value={x.id}>{x.code} — {x.name}</option>)}
          </Select>
          <Select label="Hammadde/Malzeme" value={modal.doc.material_id || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, material_id: e.target.value } })} data-testid="dev-form-material">
            <option value="">— Seç —</option>
            {materials.slice(0, 200).map(x => <option key={x.id} value={x.id}>{x.code} — {x.name}</option>)}
          </Select>
        </div>)}
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={() => setModal(null)}>Vazgeç</Btn>
          <Btn onClick={save} data-testid="dev-form-save">Kaydet</Btn>
        </div>
      </Modal>

      {/* VIEW MODAL */}
      <Modal open={!!modal && modal.mode === "view"} onClose={() => setModal(null)} title={`Sapma ${modal?.doc?.number || ""}`} size="lg">
        {modal?.doc && <>
          <DeviationDetail doc={modal.doc}/>
          <div className="mt-3"><GmpEvidencePanel module="deviation" recordId={modal.doc.id}/></div>
        </>}
      </Modal>

      {/* CLOSE MODAL */}
      <Modal open={!!closeModal} onClose={() => setCloseModal(null)} title={`Sapma Kapat: ${closeModal?.doc?.number || ""}`} size="md">
        {closeModal && (<div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
            Sapma kapatılamaz: <b>Kök Neden</b> + <b>Çözüm</b> zorunludur (ISO 22716 uyumluluğu).
          </div>
          <Textarea label="Kök Neden *" rows={3} value={closeModal.root_cause} onChange={e => setCloseModal({ ...closeModal, root_cause: e.target.value })} data-testid="dev-close-root"/>
          <Textarea label="Çözüm / Alınan Aksiyon *" rows={3} value={closeModal.resolution} onChange={e => setCloseModal({ ...closeModal, resolution: e.target.value })} data-testid="dev-close-resolution"/>
          <Textarea label="Kapatma Notları" rows={2} value={closeModal.closure_notes} onChange={e => setCloseModal({ ...closeModal, closure_notes: e.target.value })} data-testid="dev-close-notes"/>
        </div>)}
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={() => setCloseModal(null)}>Vazgeç</Btn>
          <Btn onClick={doClose} data-testid="dev-close-confirm">Kapat</Btn>
        </div>
      </Modal>

      {/* CAPA MODAL */}
      <OpenCapaModal open={!!capaModal} onClose={() => setCapaModal(null)} onConfirm={confirmCapa}
        defaultTitle={capaModal?.defaultTitle} defaultDescription={capaModal?.defaultDescription}/>
    </div>
  );
}

function DeviationDetail({ doc }) {
  return (
    <div className="space-y-2 text-sm">
      <div className="grid grid-cols-2 gap-3 text-xs">
        <KV l="No" v={doc.number}/>
        <KV l="Durum" v={<Badge level={STATUS_LEVEL[doc.status]}>{doc.status}</Badge>}/>
        <KV l="Kategori" v={DEV_CATEGORIES.find(x => x.k === doc.category)?.l || doc.category}/>
        <KV l="Kritiklik" v={<Badge level={SEVERITY_LEVEL[doc.severity]}>{doc.severity}</Badge>}/>
        <KV l="Oluşma" v={fmtDate(doc.occurred_at)}/>
        <KV l="Kaydeden" v={doc.created_by_name}/>
        {doc.department && <KV l="Departman" v={doc.department}/>}
        {doc.closed_at && <KV l="Kapatma" v={fmtDateTime(doc.closed_at)}/>}
      </div>
      <div><div className="text-[10px] uppercase text-slate-500 mb-0.5">Açıklama</div><div className="bg-slate-50 border rounded p-2 whitespace-pre-wrap">{doc.description}</div></div>
      {(doc.root_cause || doc.resolution) && <>
        <div><div className="text-[10px] uppercase text-slate-500 mb-0.5">Kök Neden</div><div className="bg-amber-50 border border-amber-200 rounded p-2 whitespace-pre-wrap">{doc.root_cause || "—"}</div></div>
        <div><div className="text-[10px] uppercase text-slate-500 mb-0.5">Çözüm</div><div className="bg-emerald-50 border border-emerald-200 rounded p-2 whitespace-pre-wrap">{doc.resolution || "—"}</div></div>
      </>}
      {(doc.capa_request_ids || []).length > 0 && (
        <div className="text-xs bg-sky-50 border border-sky-200 rounded p-2">
          <b>🛡 Bağlı CAPA'lar:</b> {doc.capa_request_ids.length} adet — GMP → CAPA sekmesinden görüntüleyin.
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        {doc.production_order_no_snapshot && <RefBox label="Üretim Emri" value={doc.production_order_no_snapshot}/>}
        {doc.lot_code_snapshot && <RefBox label="Lot" value={doc.lot_code_snapshot}/>}
        {doc.qc_project_no_snapshot && <RefBox label="QC Testi" value={`${doc.qc_project_no_snapshot} — ${doc.qc_product_name_snapshot || ""}`}/>}
        {doc.equipment_code_snapshot && <RefBox label="Ekipman" value={`${doc.equipment_code_snapshot} — ${doc.equipment_name_snapshot || ""}`}/>}
        {doc.material_code_snapshot && <RefBox label="Malzeme" value={`${doc.material_code_snapshot} — ${doc.material_name_snapshot || ""}`}/>}
      </div>
    </div>
  );
}

function KV({ l, v }) {
  return (<div className="flex items-baseline gap-2"><span className="text-slate-500">{l}:</span><span className="font-semibold">{v}</span></div>);
}
function RefBox({ label, value }) {
  return (<div className="bg-slate-50 border border-slate-200 rounded p-1.5"><div className="text-[9px] text-slate-500 uppercase">{label}</div><div className="font-mono">{value}</div></div>);
}


/* ============= COMPLAINTS TAB ============= */
export function ComplaintsTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ status: "", severity: "", complaint_type: "", q: "" });
  const [modal, setModal] = useState(null);
  const [closeModal, setCloseModal] = useState(null);
  const [capaModal, setCapaModal] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [lots, setLots] = useState([]);

  const load = async () => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    const r = await api.get("/gmp/complaints", params);
    setRows(r || []);
  };
  useEffect(() => { load(); }, [f.status, f.severity, f.complaint_type]);
  useEffect(() => {
    Promise.all([
      api.get("/customers").catch(() => []),
      api.get("/products").catch(() => []),
      api.get("/sales-orders").catch(() => []),
      api.get("/inventory/lots").catch(() => []),
    ]).then(([c, p, so, l]) => { setCustomers(c || []); setProducts(p || []); setSalesOrders(so || []); setLots(l || []); });
  }, []);

  const openCreate = () => setModal({ mode: "create", doc: { title: "", description: "", complaint_type: "quality", severity: "major", reported_at: today() } });
  const openEdit = (doc) => setModal({ mode: "edit", doc: { ...doc } });
  const save = async () => {
    const doc = modal.doc;
    if (!doc.title?.trim() || !doc.description?.trim()) { alert("Başlık ve açıklama zorunlu"); return; }
    try {
      if (modal.mode === "create") await api.post("/gmp/complaints", doc);
      else await api.put(`/gmp/complaints/${doc.id}`, doc);
      setModal(null); load();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
  };
  const invest = async (r) => { try { await api.post(`/gmp/complaints/${r.id}/investigate`); load(); } catch (e) { alert(e.response?.data?.detail); } };
  const doClose = async () => {
    const { doc, root_cause, corrective_action, customer_response, closure_notes } = closeModal;
    if (!root_cause?.trim() || !corrective_action?.trim()) { alert("Kök Neden ve Düzeltici Aksiyon zorunlu"); return; }
    try { await api.post(`/gmp/complaints/${doc.id}/close`, { root_cause, corrective_action, customer_response, closure_notes }); setCloseModal(null); load(); }
    catch (e) { alert(e.response?.data?.detail || "Kapatılamadı"); }
  };
  const del = async (r) => { if (window.confirm(`Şikayet ${r.number} arşivlensin mi?`)) { try { await api.del(`/gmp/complaints/${r.id}`); load(); } catch (e) { alert(e.response?.data?.detail); } } };
  const openCapa = (r) => setCapaModal({
    doc: r,
    defaultTitle: `CAPA — Şikayet ${r.number}: ${r.title}`,
    defaultDescription: `Şikayet No: ${r.number}\nTip: ${r.complaint_type}\nKritiklik: ${r.severity}\nMüşteri: ${r.customer_name_snapshot || r.complainant_name || "-"}\nÜrün/Lot: ${r.product_name_snapshot || "-"} / ${r.lot_code || r.lot_code_snapshot || "-"}\nAçıklama: ${r.description || "-"}`,
  });
  const confirmCapa = async ({ title, description, priority }) => {
    try {
      await api.post(`/gmp/complaints/${capaModal.doc.id}/capa`, { title, description, priority });
      alert("CAPA açıldı. GMP → CAPA sekmesinden takip edebilirsiniz."); setCapaModal(null); load();
    } catch (e) { alert(e.response?.data?.detail || "CAPA açılamadı"); }
  };

  const filtered = useMemo(() => {
    const q = f.q?.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => (r.number + " " + r.title + " " + (r.customer_name_snapshot || "") + " " + (r.lot_code || "")).toLowerCase().includes(q));
  }, [rows, f.q]);

  return (
    <div>
      <div className="flex flex-wrap items-end gap-2 mb-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
        <div className="min-w-[200px] flex-1"><Input label="Arama" value={f.q} onChange={e => setF({ ...f, q: e.target.value })} placeholder="No / müşteri / lot / başlık" data-testid="comp-search"/></div>
        <Select label="Durum" value={f.status} onChange={e => setF({ ...f, status: e.target.value })} data-testid="comp-filter-status">
          <option value="">Tümü</option><option value="open">Açık</option><option value="investigating">Soruşturmada</option>
          <option value="awaiting_customer">Müşteri Yanıtı Bekliyor</option>
          <option value="closed">Kapalı</option><option value="cancelled">İptal</option>
        </Select>
        <Select label="Kritiklik" value={f.severity} onChange={e => setF({ ...f, severity: e.target.value })} data-testid="comp-filter-severity">
          <option value="">Tümü</option>{SEVERITIES.map(s => <option key={s.k} value={s.k}>{s.l}</option>)}
        </Select>
        <Select label="Tip" value={f.complaint_type} onChange={e => setF({ ...f, complaint_type: e.target.value })} data-testid="comp-filter-type">
          <option value="">Tümü</option>{COMPLAINT_TYPES.map(c => <option key={c.k} value={c.k}>{c.l}</option>)}
        </Select>
        <div className="flex-1"/>
        {isAdmin && <Btn onClick={openCreate} data-testid="comp-new"><Plus size={14}/> Yeni Şikayet</Btn>}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-slate-100 text-slate-700 text-xs">
            <tr>
              <th className="text-left px-3 py-2">No</th>
              <th className="text-left px-3 py-2">Başlık</th>
              <th className="text-left px-3 py-2">Müşteri</th>
              <th className="text-left px-3 py-2">Ürün / Lot</th>
              <th className="text-left px-3 py-2">Tip</th>
              <th className="text-left px-3 py-2">Kritiklik</th>
              <th className="text-left px-3 py-2">Durum</th>
              <th className="text-left px-3 py-2">Tarih</th>
              <th className="text-left px-3 py-2 w-72">İşlem</th>
            </tr>
          </thead>
          <tbody data-testid="comp-table">
            {filtered.map(r => (
              <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`comp-row-${r.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.number}</td>
                <td className="px-3 py-2 font-semibold text-slate-800">{r.title}</td>
                <td className="px-3 py-2 text-xs">{r.customer_name_snapshot || r.complainant_name || "—"}</td>
                <td className="px-3 py-2 text-xs">{r.product_name_snapshot || "—"}{(r.lot_code || r.lot_code_snapshot) ? <div className="text-[10px] font-mono text-slate-500">Lot: {r.lot_code || r.lot_code_snapshot}</div> : null}</td>
                <td className="px-3 py-2 text-xs">{COMPLAINT_TYPES.find(x => x.k === r.complaint_type)?.l || r.complaint_type}</td>
                <td className="px-3 py-2"><Badge level={SEVERITY_LEVEL[r.severity]}>{SEVERITIES.find(x => x.k === r.severity)?.l || r.severity}</Badge></td>
                <td className="px-3 py-2"><Badge level={STATUS_LEVEL[r.status]}>{r.status}</Badge></td>
                <td className="px-3 py-2 text-xs">{fmtDate(r.created_at)}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    <Btn size="sm" variant="ghost" onClick={() => setModal({ mode: "view", doc: r })} data-testid={`comp-view-${r.id}`}>Detay</Btn>
                    {r.status === "open" && <Btn size="sm" variant="secondary" onClick={() => invest(r)} data-testid={`comp-investigate-${r.id}`}>Soruşturmaya Al</Btn>}
                    {r.status !== "closed" && r.status !== "cancelled" && <Btn size="sm" onClick={() => setCloseModal({ doc: r, root_cause: r.root_cause || "", corrective_action: r.corrective_action || "", customer_response: r.customer_response || "", closure_notes: "" })} data-testid={`comp-close-${r.id}`}>Kapat</Btn>}
                    {r.status !== "closed" && <Btn size="sm" variant="secondary" onClick={() => openCapa(r)} data-testid={`comp-capa-${r.id}`}><Shield size={11}/> CAPA Aç</Btn>}
                    {r.status !== "closed" && r.status !== "cancelled" && <Btn size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`comp-edit-${r.id}`}>Düzenle</Btn>}
                    {/* Iter 202 §2 — Şikayet controlled PDF (preview-first). */}
                    <Btn size="sm" variant="ghost" onClick={async () => {
                      try {
                        const blob = await api.getBlob(`/gmp/complaints/${r.id}/pdf`);
                        openPdfPreview(blob, `Sikayet_${r.number || r.id}.pdf`);
                      } catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); }
                    }} data-testid={`comp-pdf-${r.id}`} title="Şikayet PDF Önizleme"><FileText size={12}/></Btn>
                    <Btn size="sm" variant="danger" onClick={() => del(r)} data-testid={`comp-delete-${r.id}`}>Sil</Btn>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-slate-400">Şikayet kaydı yok.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={!!modal && modal.mode !== "view"} onClose={() => setModal(null)} title={modal?.mode === "create" ? "Yeni Şikayet" : `Şikayet Düzenle ${modal?.doc?.number || ""}`} size="lg">
        {modal && (<div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Input label="Başlık *" value={modal.doc.title} onChange={e => setModal({ ...modal, doc: { ...modal.doc, title: e.target.value } })} data-testid="comp-form-title"/></div>
          <div className="col-span-2"><Textarea label="Açıklama *" rows={3} value={modal.doc.description} onChange={e => setModal({ ...modal, doc: { ...modal.doc, description: e.target.value } })} data-testid="comp-form-desc"/></div>
          <Select label="Şikayet Tipi" value={modal.doc.complaint_type} onChange={e => setModal({ ...modal, doc: { ...modal.doc, complaint_type: e.target.value } })} data-testid="comp-form-type">
            {COMPLAINT_TYPES.map(c => <option key={c.k} value={c.k}>{c.l}</option>)}
          </Select>
          <Select label="Kritiklik" value={modal.doc.severity} onChange={e => setModal({ ...modal, doc: { ...modal.doc, severity: e.target.value } })} data-testid="comp-form-severity">
            {SEVERITIES.map(s => <option key={s.k} value={s.k}>{s.l}</option>)}
          </Select>
          <Input label="Bildirim Tarihi" type="date" value={modal.doc.reported_at || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, reported_at: e.target.value } })} data-testid="comp-form-reported"/>
          <Input label="Şikayetçi Adı" value={modal.doc.complainant_name || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, complainant_name: e.target.value } })} data-testid="comp-form-complainant"/>
          <div className="col-span-2 border-t pt-2 mt-1 text-xs text-slate-500 font-semibold uppercase">Bağlı Kayıtlar (opsiyonel)</div>
          <Select label="Müşteri" value={modal.doc.customer_id || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, customer_id: e.target.value } })} data-testid="comp-form-customer">
            <option value="">— Seç —</option>
            {customers.slice(0, 500).map(c => <option key={c.id} value={c.id}>{c.legal_name || c.name}</option>)}
          </Select>
          <Select label="Satış Emri" value={modal.doc.sales_order_id || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, sales_order_id: e.target.value } })} data-testid="comp-form-so">
            <option value="">— Seç —</option>
            {salesOrders.slice(0, 300).map(s => <option key={s.id} value={s.id}>{s.so_no}</option>)}
          </Select>
          <Select label="Ürün" value={modal.doc.product_id || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, product_id: e.target.value } })} data-testid="comp-form-product">
            <option value="">— Seç —</option>
            {products.slice(0, 500).map(p => <option key={p.id} value={p.id}>{p.name} {p.code ? `(${p.code})` : ""}</option>)}
          </Select>
          <Select label="Lot" value={modal.doc.lot_id || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, lot_id: e.target.value } })} data-testid="comp-form-lot">
            <option value="">— Seç —</option>
            {lots.slice(0, 500).map(l => <option key={l.id} value={l.id}>{l.lot_code}</option>)}
          </Select>
          <Input label="Manuel Lot Kodu (bağımsız)" value={modal.doc.lot_code || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, lot_code: e.target.value } })} data-testid="comp-form-lotcode"/>
        </div>)}
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={() => setModal(null)}>Vazgeç</Btn>
          <Btn onClick={save} data-testid="comp-form-save">Kaydet</Btn>
        </div>
      </Modal>

      <Modal open={!!modal && modal.mode === "view"} onClose={() => setModal(null)} title={`Şikayet ${modal?.doc?.number || ""}`} size="lg">
        {modal?.doc && <>
          <ComplaintDetail doc={modal.doc}/>
          <div className="mt-3"><GmpEvidencePanel module="complaint" recordId={modal.doc.id}/></div>
        </>}
      </Modal>

      <Modal open={!!closeModal} onClose={() => setCloseModal(null)} title={`Şikayet Kapat: ${closeModal?.doc?.number || ""}`} size="md">
        {closeModal && (<div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">Kapatma için <b>Kök Neden</b> + <b>Düzeltici Aksiyon</b> zorunludur.</div>
          <Textarea label="Kök Neden *" rows={3} value={closeModal.root_cause} onChange={e => setCloseModal({ ...closeModal, root_cause: e.target.value })} data-testid="comp-close-root"/>
          <Textarea label="Düzeltici Aksiyon *" rows={3} value={closeModal.corrective_action} onChange={e => setCloseModal({ ...closeModal, corrective_action: e.target.value })} data-testid="comp-close-action"/>
          <Textarea label="Müşteri Cevabı" rows={2} value={closeModal.customer_response} onChange={e => setCloseModal({ ...closeModal, customer_response: e.target.value })} data-testid="comp-close-response"/>
          <Textarea label="Kapatma Notları" rows={2} value={closeModal.closure_notes} onChange={e => setCloseModal({ ...closeModal, closure_notes: e.target.value })} data-testid="comp-close-notes"/>
        </div>)}
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={() => setCloseModal(null)}>Vazgeç</Btn>
          <Btn onClick={doClose} data-testid="comp-close-confirm">Kapat</Btn>
        </div>
      </Modal>

      <OpenCapaModal open={!!capaModal} onClose={() => setCapaModal(null)} onConfirm={confirmCapa}
        defaultTitle={capaModal?.defaultTitle} defaultDescription={capaModal?.defaultDescription}/>
    </div>
  );
}

function ComplaintDetail({ doc }) {
  return (
    <div className="space-y-2 text-sm">
      <div className="grid grid-cols-2 gap-3 text-xs">
        <KV l="No" v={doc.number}/>
        <KV l="Durum" v={<Badge level={STATUS_LEVEL[doc.status]}>{doc.status}</Badge>}/>
        <KV l="Tip" v={COMPLAINT_TYPES.find(x => x.k === doc.complaint_type)?.l || doc.complaint_type}/>
        <KV l="Kritiklik" v={<Badge level={SEVERITY_LEVEL[doc.severity]}>{doc.severity}</Badge>}/>
        <KV l="Bildirim" v={fmtDate(doc.reported_at)}/>
        <KV l="Kaydeden" v={doc.created_by_name}/>
      </div>
      <div><div className="text-[10px] uppercase text-slate-500 mb-0.5">Açıklama</div><div className="bg-slate-50 border rounded p-2 whitespace-pre-wrap">{doc.description}</div></div>
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        {(doc.customer_name_snapshot || doc.complainant_name) && <RefBox label="Müşteri" value={doc.customer_name_snapshot || doc.complainant_name}/>}
        {doc.product_name_snapshot && <RefBox label="Ürün" value={doc.product_name_snapshot}/>}
        {(doc.lot_code_snapshot || doc.lot_code) && <RefBox label="Lot" value={doc.lot_code_snapshot || doc.lot_code}/>}
        {doc.sales_order_no_snapshot && <RefBox label="Satış Emri" value={doc.sales_order_no_snapshot}/>}
        {doc.shipment_no_snapshot && <RefBox label="Sevkiyat" value={doc.shipment_no_snapshot}/>}
      </div>
      {(doc.root_cause || doc.corrective_action) && <>
        <div><div className="text-[10px] uppercase text-slate-500 mb-0.5">Kök Neden</div><div className="bg-amber-50 border border-amber-200 rounded p-2 whitespace-pre-wrap">{doc.root_cause || "—"}</div></div>
        <div><div className="text-[10px] uppercase text-slate-500 mb-0.5">Düzeltici Aksiyon</div><div className="bg-emerald-50 border border-emerald-200 rounded p-2 whitespace-pre-wrap">{doc.corrective_action || "—"}</div></div>
        {doc.customer_response && <div><div className="text-[10px] uppercase text-slate-500 mb-0.5">Müşteri Cevabı</div><div className="bg-sky-50 border border-sky-200 rounded p-2 whitespace-pre-wrap">{doc.customer_response}</div></div>}
      </>}
      {(doc.capa_request_ids || []).length > 0 && <div className="text-xs bg-sky-50 border border-sky-200 rounded p-2"><b>🛡 Bağlı CAPA'lar:</b> {doc.capa_request_ids.length} adet</div>}
    </div>
  );
}


/* ============= OOS TAB ============= */
export function OOSTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ status: "", disposition: "", q: "" });
  const [modal, setModal] = useState(null); // create / edit / view
  const [phaseModal, setPhaseModal] = useState(null);
  const [closeModal, setCloseModal] = useState(null);
  const [capaModal, setCapaModal] = useState(null);
  const [qcCandidates, setQcCandidates] = useState([]);

  const load = async () => {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    const r = await api.get("/gmp/oos", params);
    setRows(r || []);
  };
  useEffect(() => { load(); }, [f.status, f.disposition]);
  useEffect(() => {
    // Only completed + uygun_degil QC tests are eligible for OOS
    api.get("/qc-tests", { status: "TAMAMLANDI" }).then(r => {
      setQcCandidates((r || []).filter(t => t.result === "uygun_degil"));
    }).catch(() => setQcCandidates([]));
  }, []);

  const openCreate = () => setModal({ mode: "create", doc: { qc_test_id: "", test_parameter: "", measured_value: "", spec_min: "", spec_max: "", description: "", severity: "major" } });
  const save = async () => {
    const doc = modal.doc;
    if (!doc.qc_test_id) { alert("QC testi seçin"); return; }
    if (!doc.test_parameter?.trim()) { alert("Test parametresi zorunlu"); return; }
    try {
      if (modal.mode === "create") await api.post("/gmp/oos", doc);
      else await api.put(`/gmp/oos/${doc.id}`, doc);
      setModal(null); load();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
  };
  const doPhase = async () => {
    const { doc, phase, findings } = phaseModal;
    if (!findings?.trim()) { alert("Bulgular zorunlu"); return; }
    try { await api.post(`/gmp/oos/${doc.id}/${phase}`, { findings }); setPhaseModal(null); load(); }
    catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
  };
  const doClose = async () => {
    const { doc, root_cause_confirmed, disposition, closure_notes } = closeModal;
    if (!root_cause_confirmed?.trim()) { alert("Doğrulanmış Kök Neden zorunlu"); return; }
    if (!disposition || disposition === "pending") { alert("Disposition seçin"); return; }
    try { await api.post(`/gmp/oos/${doc.id}/close`, { root_cause_confirmed, disposition, closure_notes }); setCloseModal(null); load(); }
    catch (e) { alert(e.response?.data?.detail || "Kapatılamadı"); }
  };
  const del = async (r) => { if (window.confirm(`OOS ${r.number} arşivlensin mi?`)) { try { await api.del(`/gmp/oos/${r.id}`); load(); } catch (e) { alert(e.response?.data?.detail); } } };
  const openCapa = (r) => setCapaModal({
    doc: r,
    defaultTitle: `CAPA — OOS ${r.number}: ${r.test_parameter}`,
    defaultDescription: `OOS No: ${r.number}\nParametre: ${r.test_parameter}\nÖlçüm: ${r.measured_value || "-"}  ·  Spec: [${r.spec_min || "-"} — ${r.spec_max || "-"}]\nQC Proje: ${r.qc_project_no_snapshot || "-"} · Ürün: ${r.qc_product_name_snapshot || "-"}\nAçıklama: ${r.description || "-"}`,
  });
  const confirmCapa = async ({ title, description, priority }) => {
    try {
      await api.post(`/gmp/oos/${capaModal.doc.id}/capa`, { title, description, priority });
      alert("CAPA açıldı. GMP → CAPA sekmesinden takip edebilirsiniz."); setCapaModal(null); load();
    } catch (e) { alert(e.response?.data?.detail || "CAPA açılamadı"); }
  };

  const filtered = useMemo(() => {
    const q = f.q?.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => (r.number + " " + r.test_parameter + " " + (r.qc_project_no_snapshot || "") + " " + (r.description || "")).toLowerCase().includes(q));
  }, [rows, f.q]);

  return (
    <div>
      <div className="bg-amber-50/70 border border-amber-200 rounded-lg p-3 mb-3 text-xs text-amber-900">
        <b>ℹ OOS (Out of Specification):</b> Yalnızca <b>TAMAMLANDI</b> + <b>Uygun Değil</b> sonucu olan QC testleri için elle açılır.
        Bir QC testi × parametre kombinasyonu için aynı anda yalnızca <b>tek aktif OOS</b> olabilir (idempotent).
        OOS açılması QC karar mantığını (karantina/serbest/red) değiştirmez — sadece kayıt altına alır.
      </div>
      <div className="flex flex-wrap items-end gap-2 mb-3 bg-slate-50 border border-slate-200 rounded-lg p-3">
        <div className="min-w-[200px] flex-1"><Input label="Arama" value={f.q} onChange={e => setF({ ...f, q: e.target.value })} placeholder="No / parametre / proje" data-testid="oos-search"/></div>
        <Select label="Durum" value={f.status} onChange={e => setF({ ...f, status: e.target.value })} data-testid="oos-filter-status">
          <option value="">Tümü</option><option value="open">Açık</option><option value="phase_1">Faz 1</option>
          <option value="phase_2">Faz 2</option><option value="closed">Kapalı</option><option value="cancelled">İptal</option>
        </Select>
        <Select label="Disposition" value={f.disposition} onChange={e => setF({ ...f, disposition: e.target.value })} data-testid="oos-filter-disp">
          <option value="">Tümü</option>{OOS_DISPOSITIONS.map(d => <option key={d.k} value={d.k}>{d.l}</option>)}
        </Select>
        <div className="flex-1"/>
        {isAdmin && <Btn onClick={openCreate} data-testid="oos-new"><Plus size={14}/> Yeni OOS</Btn>}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-slate-100 text-slate-700 text-xs">
            <tr>
              <th className="text-left px-3 py-2">No</th>
              <th className="text-left px-3 py-2">Parametre</th>
              <th className="text-left px-3 py-2">Ölçüm / Spec</th>
              <th className="text-left px-3 py-2">QC Testi</th>
              <th className="text-left px-3 py-2">Durum</th>
              <th className="text-left px-3 py-2">Disposition</th>
              <th className="text-left px-3 py-2">Tarih</th>
              <th className="text-left px-3 py-2 w-80">İşlem</th>
            </tr>
          </thead>
          <tbody data-testid="oos-table">
            {filtered.map(r => (
              <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`oos-row-${r.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.number}</td>
                <td className="px-3 py-2 font-semibold text-slate-800">{r.test_parameter}</td>
                <td className="px-3 py-2 text-xs">
                  <div className="font-mono">{r.measured_value || "—"}</div>
                  <div className="text-slate-500 text-[10px]">[{r.spec_min || "—"} → {r.spec_max || "—"}]</div>
                </td>
                <td className="px-3 py-2 text-xs">{r.qc_project_no_snapshot || "—"}{r.qc_product_name_snapshot ? <div className="text-slate-500 text-[10px]">{r.qc_product_name_snapshot}</div> : null}</td>
                <td className="px-3 py-2"><Badge level={STATUS_LEVEL[r.status]}>{r.status}</Badge></td>
                <td className="px-3 py-2 text-xs">{OOS_DISPOSITIONS.find(x => x.k === r.disposition)?.l || r.disposition}</td>
                <td className="px-3 py-2 text-xs">{fmtDate(r.created_at)}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    <Btn size="sm" variant="ghost" onClick={() => setModal({ mode: "view", doc: r })} data-testid={`oos-view-${r.id}`}>Detay</Btn>
                    {(r.status === "open" || r.status === "phase_1") && <Btn size="sm" variant="secondary" onClick={() => setPhaseModal({ doc: r, phase: "phase1", findings: r.phase_1_investigation || "" })} data-testid={`oos-phase1-${r.id}`}>Faz 1</Btn>}
                    {(r.status === "phase_1" || r.status === "phase_2") && <Btn size="sm" variant="secondary" onClick={() => setPhaseModal({ doc: r, phase: "phase2", findings: r.phase_2_investigation || "" })} data-testid={`oos-phase2-${r.id}`}>Faz 2</Btn>}
                    {r.status !== "closed" && r.status !== "cancelled" && <Btn size="sm" onClick={() => setCloseModal({ doc: r, root_cause_confirmed: r.root_cause_confirmed || "", disposition: r.disposition === "pending" ? "release" : r.disposition, closure_notes: "" })} data-testid={`oos-close-${r.id}`}>Kapat</Btn>}
                    {r.status !== "closed" && <Btn size="sm" variant="secondary" onClick={() => openCapa(r)} data-testid={`oos-capa-${r.id}`}><Shield size={11}/> CAPA Aç</Btn>}
                    <Btn size="sm" variant="danger" onClick={() => del(r)} data-testid={`oos-delete-${r.id}`}>Sil</Btn>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-400">OOS kaydı yok.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={!!modal && modal.mode !== "view"} onClose={() => setModal(null)} title={modal?.mode === "create" ? "Yeni OOS" : `OOS Düzenle ${modal?.doc?.number || ""}`} size="lg">
        {modal && (<div className="grid grid-cols-2 gap-3">
          {modal.mode === "create" ? (
            <div className="col-span-2">
              <Select label="QC Testi (Tamamlanmış + Uygun Değil) *" value={modal.doc.qc_test_id} onChange={e => setModal({ ...modal, doc: { ...modal.doc, qc_test_id: e.target.value } })} data-testid="oos-form-qc">
                <option value="">— Seç —</option>
                {qcCandidates.map(t => <option key={t.id} value={t.id}>{t.project_no_snapshot} · {t.test_type_label || t.test_type} · {t.product_name_snapshot} · Sonuç: Uygun Değil</option>)}
              </Select>
              {qcCandidates.length === 0 && <div className="text-xs text-slate-500 italic mt-1">Uygun QC testi yok. QC modülünde bir testi TAMAMLANDI + Uygun Değil sonuçla kapattıktan sonra OOS açabilirsiniz.</div>}
            </div>
          ) : (
            <div className="col-span-2 text-xs text-slate-500 bg-slate-50 border rounded p-2">QC Testi: <b>{modal.doc.qc_project_no_snapshot || "—"}</b> · {modal.doc.qc_product_name_snapshot || "—"}</div>
          )}
          <div className="col-span-2"><Input label="Test Parametresi *" value={modal.doc.test_parameter} onChange={e => setModal({ ...modal, doc: { ...modal.doc, test_parameter: e.target.value } })} placeholder="Örn: pH, Mikrobiyoloji, Yoğunluk" data-testid="oos-form-param"/></div>
          <Input label="Ölçülen Değer" value={modal.doc.measured_value || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, measured_value: e.target.value } })} data-testid="oos-form-measured"/>
          <Select label="Kritiklik" value={modal.doc.severity} onChange={e => setModal({ ...modal, doc: { ...modal.doc, severity: e.target.value } })} data-testid="oos-form-severity">
            {SEVERITIES.map(s => <option key={s.k} value={s.k}>{s.l}</option>)}
          </Select>
          <Input label="Spec Alt" value={modal.doc.spec_min || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, spec_min: e.target.value } })} data-testid="oos-form-spec-min"/>
          <Input label="Spec Üst" value={modal.doc.spec_max || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, spec_max: e.target.value } })} data-testid="oos-form-spec-max"/>
          <div className="col-span-2"><Textarea label="Açıklama" rows={3} value={modal.doc.description || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, description: e.target.value } })} data-testid="oos-form-desc"/></div>
        </div>)}
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={() => setModal(null)}>Vazgeç</Btn>
          <Btn onClick={save} data-testid="oos-form-save">Kaydet</Btn>
        </div>
      </Modal>

      <Modal open={!!modal && modal.mode === "view"} onClose={() => setModal(null)} title={`OOS ${modal?.doc?.number || ""}`} size="lg">
        {modal?.doc && <>
          <OOSDetail doc={modal.doc}/>
          <div className="mt-3"><GmpEvidencePanel module="oos" recordId={modal.doc.id}/></div>
        </>}
      </Modal>

      <Modal open={!!phaseModal} onClose={() => setPhaseModal(null)} title={`${phaseModal?.phase === "phase1" ? "Faz 1 Soruşturma" : "Faz 2 Soruşturma"} — ${phaseModal?.doc?.number || ""}`} size="md">
        {phaseModal && (<div className="space-y-3">
          <div className="bg-sky-50 border border-sky-200 rounded p-2 text-xs">
            {phaseModal.phase === "phase1"
              ? <>Faz 1: Laboratuvar hata analizi (yöntem, kalibrasyon, hesaplama, örneklem)</>
              : <>Faz 2: Üretim / Formülasyon / Sistem araştırması. Faz 1 tamamlanmış olmalı.</>}
          </div>
          <Textarea label="Bulgular *" rows={5} value={phaseModal.findings} onChange={e => setPhaseModal({ ...phaseModal, findings: e.target.value })} data-testid="oos-phase-findings"/>
        </div>)}
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={() => setPhaseModal(null)}>Vazgeç</Btn>
          <Btn onClick={doPhase} data-testid="oos-phase-confirm">Kaydet</Btn>
        </div>
      </Modal>

      <Modal open={!!closeModal} onClose={() => setCloseModal(null)} title={`OOS Kapat: ${closeModal?.doc?.number || ""}`} size="md">
        {closeModal && (<div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">Kapatma için <b>Doğrulanmış Kök Neden</b> + <b>Disposition</b> zorunludur.</div>
          <Textarea label="Doğrulanmış Kök Neden *" rows={3} value={closeModal.root_cause_confirmed} onChange={e => setCloseModal({ ...closeModal, root_cause_confirmed: e.target.value })} data-testid="oos-close-root"/>
          <Select label="Disposition *" value={closeModal.disposition} onChange={e => setCloseModal({ ...closeModal, disposition: e.target.value })} data-testid="oos-close-disp">
            {OOS_DISPOSITIONS.filter(d => d.k !== "pending").map(d => <option key={d.k} value={d.k}>{d.l}</option>)}
          </Select>
          <Textarea label="Kapatma Notları" rows={2} value={closeModal.closure_notes} onChange={e => setCloseModal({ ...closeModal, closure_notes: e.target.value })} data-testid="oos-close-notes"/>
        </div>)}
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={() => setCloseModal(null)}>Vazgeç</Btn>
          <Btn onClick={doClose} data-testid="oos-close-confirm">Kapat</Btn>
        </div>
      </Modal>

      <OpenCapaModal open={!!capaModal} onClose={() => setCapaModal(null)} onConfirm={confirmCapa}
        defaultTitle={capaModal?.defaultTitle} defaultDescription={capaModal?.defaultDescription}/>
    </div>
  );
}

function OOSDetail({ doc }) {
  return (
    <div className="space-y-2 text-sm">
      <div className="grid grid-cols-2 gap-3 text-xs">
        <KV l="No" v={doc.number}/>
        <KV l="Durum" v={<Badge level={STATUS_LEVEL[doc.status]}>{doc.status}</Badge>}/>
        <KV l="Parametre" v={doc.test_parameter}/>
        <KV l="Disposition" v={OOS_DISPOSITIONS.find(x => x.k === doc.disposition)?.l || doc.disposition}/>
        <KV l="Ölçüm" v={doc.measured_value || "—"}/>
        <KV l="Spec" v={`[${doc.spec_min || "—"} → ${doc.spec_max || "—"}]`}/>
        <KV l="QC Proje" v={doc.qc_project_no_snapshot || "—"}/>
        <KV l="Ürün" v={doc.qc_product_name_snapshot || "—"}/>
        {doc.sample_lot_snapshot && <KV l="Numune Lot" v={doc.sample_lot_snapshot}/>}
        {doc.closed_at && <KV l="Kapatma" v={fmtDateTime(doc.closed_at)}/>}
      </div>
      <div><div className="text-[10px] uppercase text-slate-500 mb-0.5">Açıklama</div><div className="bg-slate-50 border rounded p-2 whitespace-pre-wrap">{doc.description || "—"}</div></div>
      {doc.phase_1_investigation && <div><div className="text-[10px] uppercase text-sky-600 mb-0.5">Faz 1 Bulguları</div><div className="bg-sky-50 border border-sky-200 rounded p-2 whitespace-pre-wrap">{doc.phase_1_investigation}</div></div>}
      {doc.phase_2_investigation && <div><div className="text-[10px] uppercase text-indigo-600 mb-0.5">Faz 2 Bulguları</div><div className="bg-indigo-50 border border-indigo-200 rounded p-2 whitespace-pre-wrap">{doc.phase_2_investigation}</div></div>}
      {doc.root_cause_confirmed && <div><div className="text-[10px] uppercase text-amber-700 mb-0.5">Doğrulanmış Kök Neden</div><div className="bg-amber-50 border border-amber-200 rounded p-2 whitespace-pre-wrap">{doc.root_cause_confirmed}</div></div>}
      {(doc.capa_request_ids || []).length > 0 && <div className="text-xs bg-sky-50 border border-sky-200 rounded p-2"><b>🛡 Bağlı CAPA'lar:</b> {doc.capa_request_ids.length} adet</div>}
    </div>
  );
}
